import { motion } from "framer-motion";
import type { ReactElement, ReactNode } from "react";
import { FontWeight, getFontFamily } from "@/theme/typography";
import { getRadiusCssValue } from "@/theme/radius";
import { pxToRem } from "@/theme/spacing";
import { CmarkdownLabelText } from "../Cmarkdowntext/CmarkdownLabelText";
import { Cscrollable } from "../Cscrollable";
import type { KanbanColumn as KanbanColumnModel } from "./parse-mermaid-kanban";
import { KanbanCard } from "./KanbanCard";
import { COLUMN_COLORS, COLUMN_MAX_HEIGHT, COLUMN_WIDTH } from "./kanban-constants";
import { toCardKey } from "./kanban-selectors";
import type { SetColumnRefInput } from "./useKanbanBoardScroll";
import { CARD_MIN_HEIGHT, CARD_STACK_GAP, COLUMN_BACKGROUND, COLUMN_BORDER_SHADOW, COLUMN_COUNT_CHIP_BACKGROUND, COLUMN_COUNT_CHIP_BORDER, SCROLL_DIRECTION_VERTICAL, } from "./kanban-view-constants";
interface KanbanColumnProps {
    column: KanbanColumnModel;
    columnIndex: number;
    activeHoveredCardKey: string | null;
    setColumnRef: (input: SetColumnRefInput) => void;
    onHoverCard: (cardKey: string) => void;
    clearHoveredCard: () => void;
}
interface KanbanColumnShellProps {
    columnId: string;
    columnIndex: number;
    setColumnRef: (input: SetColumnRefInput) => void;
    children: ReactNode;
}
interface KanbanColumnBodyProps {
    children: ReactNode;
}
interface KanbanColumnHeaderProps {
    columnLabel: string;
    columnColor: string;
    cardCount: number;
}
interface KanbanColumnCountChipProps {
    cardCount: number;
}
interface KanbanCardListProps {
    column: KanbanColumnModel;
    columnIndex: number;
    activeHoveredCardKey: string | null;
    onHoverCard: (cardKey: string) => void;
    clearHoveredCard: () => void;
}
export function KanbanColumn({ column, columnIndex, activeHoveredCardKey, setColumnRef, onHoverCard, clearHoveredCard, }: KanbanColumnProps): ReactElement {
    const columnColor = COLUMN_COLORS[columnIndex % COLUMN_COLORS.length];
    return (
      <KanbanColumnShell columnId={column.id} columnIndex={columnIndex} setColumnRef={setColumnRef}>
        <KanbanColumnBody>
          <KanbanColumnHeader columnLabel={column.label} columnColor={columnColor} cardCount={column.cards.length}/>

      <KanbanCardList column={column} columnIndex={columnIndex} activeHoveredCardKey={activeHoveredCardKey} onHoverCard={onHoverCard} clearHoveredCard={clearHoveredCard}/>
        </KanbanColumnBody>
      </KanbanColumnShell>
    );
}
function KanbanColumnShell({ columnId, columnIndex, setColumnRef, children, }: KanbanColumnShellProps): ReactElement {
    return (<motion.div key={columnId} ref={(columnNode) => {
            setColumnRef({
                columnIndex,
                columnNode,
            });
        }} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{
            duration: 0.28,
            delay: columnIndex * 0.06,
            ease: "easeOut",
        }} style={{
            width: pxToRem(COLUMN_WIDTH),
            flexShrink: 0,
            maxHeight: pxToRem(COLUMN_MAX_HEIGHT),
            height: "100%",
            display: "flex",
            flexDirection: "column",
            minHeight: CARD_MIN_HEIGHT,
            scrollSnapAlign: "start",
            scrollSnapStop: "always",
        }}>
      {children}
    </motion.div>);
}
function KanbanColumnBody({ children, }: KanbanColumnBodyProps): ReactElement {
    return (<div style={{
            width: "100%",
            height: "100%",
            minHeight: CARD_MIN_HEIGHT,
            display: "flex",
            flexDirection: "column",
            gap: pxToRem(10),
            padding: `${pxToRem(14)} ${pxToRem(12)} ${pxToRem(12)}`,
            borderRadius: getRadiusCssValue(10),
            backgroundImage: COLUMN_BACKGROUND,
            boxShadow: COLUMN_BORDER_SHADOW,
        }}>
      {children}
    </div>);
}
function KanbanColumnHeader({ columnLabel, columnColor, cardCount, }: KanbanColumnHeaderProps): ReactElement {
    return (<div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: pxToRem(10),
            flexShrink: 0,
        }}>
      <CmarkdownLabelText dataElScope="Cmarkdownkanban/KanbanColumnHeader" content={columnLabel} size={14} weight={FontWeight.Heavy} style={{ minWidth: 0, overflow: "hidden" }} paragraphStyle={{ marginBottom: 0, minWidth: 0 }} textStyle={{
            lineHeight: 1.1,
            color: columnColor,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
        }}/>

      <KanbanColumnCountChip cardCount={cardCount}/>
    </div>);
}
function KanbanColumnCountChip({ cardCount, }: KanbanColumnCountChipProps): ReactElement {
    return (<span style={{
            minWidth: pxToRem(20),
            height: pxToRem(20),
            padding: `0 ${pxToRem(5)}`,
            borderRadius: getRadiusCssValue(999),
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: pxToRem(10),
            fontWeight: 800,
            backgroundColor: COLUMN_COUNT_CHIP_BACKGROUND,
            boxShadow: COLUMN_COUNT_CHIP_BORDER,
            color: "rgba(255,255,255,0.75)",
            fontFamily: getFontFamily(),
            fontVariantNumeric: "tabular-nums",
            flexShrink: 0,
        }}>
      {String(cardCount)}
    </span>);
}
function KanbanCardList({ column, columnIndex, activeHoveredCardKey, onHoverCard, clearHoveredCard, }: KanbanCardListProps): ReactElement {
    return (<Cscrollable dataElScope="Cmarkdownkanban/KanbanCardList" direction={SCROLL_DIRECTION_VERTICAL} fadeSize={20} verticalFadeTopSize={26} verticalFadeBottomSize={36} style={{
            width: "100%",
            minHeight: 0,
            flex: 1,
        }}>
      <div style={{
            display: "flex",
            flexDirection: "column",
            gap: CARD_STACK_GAP,
            width: "100%",
        }}>
        {column.cards.length === 0 ? (<KanbanEmptyState/>) : (column.cards.map((card, cardIndex) => {
            const cardKey = toCardKey({
                columnId: column.id,
                cardId: card.id,
            });
            const isHovered = activeHoveredCardKey === cardKey;
            const isDimmed = activeHoveredCardKey != null &&
                activeHoveredCardKey !== cardKey;
            return (<KanbanCard key={cardKey} card={card} cardKey={cardKey} columnIndex={columnIndex} cardIndex={cardIndex} isHovered={isHovered} isDimmed={isDimmed} onHoverCard={onHoverCard} clearHoveredCard={clearHoveredCard}/>);
        }))}
      </div>
    </Cscrollable>);
}
function KanbanEmptyState(): ReactElement {
    return (<div data-el="Cmarkdownkanban/EmptyState/Status" style={{
            paddingTop: pxToRem(4),
            fontSize: pxToRem(13),
            fontWeight: FontWeight.Regular,
            color: "rgba(255,255,255,0.25)",
            fontStyle: "italic",
        }}>
      No items
    </div>);
}
